import { createReducer, createActions } from 'reduxsauce'
import Immutable, { type ImmutableObject } from 'seamless-immutable'
import DeviceInfo from 'APP/Helpers/DeviceInfo'
import { PublicComputerType } from 'APP/Lib/Enums'

import type { LoginState } from 'APP/Store/Login/types'

/* ------------- Types and Action Creators ------------- */

const actionDefs = {
  rehydrateReducer: ['entries'],
  // Login
  loginRequest: [],
  loginRequestCancelled: null,
  loginRequestWithConnection: ['connection', 'options'],
  silentLoginRequestWithConnection: ['connection', 'options'],
  loginRequestWithExchangeToken: ['exchangeToken', 'options'],
  signupRequest: [],
  signupRequestWithConnection: ['connection'],
  loginSuccess: [
    'username',
    'idToken',
    'accessToken',
    'refreshToken',
    'tokenExpiration',
    'customClaims',
    'secondaryAccessToken',
    'exchangeToken',
  ],
  clearSecondaryAccessToken: null,
  loginFetchComplete: null,
  loginFailure: ['error'],
  accountLinkLoginRequest: ['loginHint'],
  setConnection: ['connection'],
  clearConnection: null,

  // Email Verification
  requestEmailVerificationCheck: null,
  emailVerificationCheck: ['silent'],
  emailVerificationCheckFailure: null,
  emailVerificationCheckSuccess: null,

  // Resend Verification Email
  emailVerificationResendRequest: null,
  emailVerificationResendSuccess: null,
  emailVerificationResendFailure: ['error'],

  setEmailVerificationBlock: ['emailVerificationBlock'],

  // Toggle MFA
  mandatoryMfaRequest: null,
  toggleMfaRequest: null,
  toggleMfaSuccess: null,
  toggleMfaFailure: null,

  // Setters
  setUsername: ['username'],
  setMessagingCredentials: ['userData'],
  refreshAuthSuccess: ['idToken', 'accessToken', 'refreshToken', 'tokenExpiration', 'customClaims'],
  refreshAuthFailure: ['error'],

  // Misc Actions
  logout: ['reason', 'unregisterPushToken', 'embeddedLogoutRequestId'],
  startRefreshAuthPoll: null,
  stopRefreshAuthPoll: null,
  clearState: null,
  setIsLoggingIn: ['data'],
  setIsLoggingOut: ['data'],
  setIsOnboardingInProgress: ['data'],

  //public computer
  submitPublicComputer: ['data'],
  updateLastActivity: null,
} as const satisfies Record<string, string[] | null>

type TActions = typeof actionDefs
type TPayloads = {}

const { Types, Creators } = createActions<TPayloads, TActions>(actionDefs)

export const LoginTypes = Types
export default Creators

type ImmutableLoginState = ImmutableObject<LoginState>

/* ------------- Initial State ------------- */

export const INITIAL_STATE: ImmutableLoginState = Immutable<LoginState>({
  username: null,
  // based on success action remove password from the store
  password: undefined,
  error: null,
  fetching: false,
  idToken: null,
  accessToken: null,
  secondaryAccessToken: null,
  exchangeToken: null,
  refreshToken: null,
  tokenExpiration: null,
  emailVerificationCheckSilent: false,
  emailVerificationRunning: false,
  emailVerificationResendRunning: false,
  emailVerificationResendFailed: false,
  creationTime: null,
  customClaims: {},
  messagingUserId: null,
  messagingTeamId: null,
  messagingToken: null,
  messagingCookie: null,
  signupFormConfig: {},
  signupProfileData: null,
  toggleMfaRequestRunning: false,
  toggleMfaRequestFailed: false,
  connection: null,
  clearState: null,
  isLoggingIn: false,
  isLoggingOut: false,
  isOnboardingInProgress: false,

  publicComputer: PublicComputerType.NO_ANSWER,
  deviceId: null,
  lastActivity: null,

  emailVerificationBlock: false,

  hydrated: false,
})

/* ------------- Reducers ------------- */

export const rehydrateReducer = (
  state: ImmutableLoginState,
  { entries }: { entries: Partial<LoginState> }
) => state.merge(entries).merge({ hydrated: true })

export const loginRequest = (state: ImmutableLoginState) =>
  state.merge({ fetching: true, error: null })

export const loginRequestWithConnection = (state: ImmutableLoginState) =>
  state.merge({ fetching: true, error: null })

export const loginRequestWithExchangeToken = (state: ImmutableLoginState) =>
  state.merge({ fetching: true, error: null })

export const signupRequest = (state: ImmutableLoginState) =>
  state.merge({ fetching: true, error: null })

export const clearSecondaryAccessToken = (state: ImmutableLoginState) =>
  state.merge({ secondaryAccessToken: null })

export const success = (
  state: ImmutableLoginState,
  {
    username,
    idToken,
    accessToken,
    refreshToken,
    tokenExpiration,
    customClaims = {},
    secondaryAccessToken = null,
    exchangeToken = null,
  }: {
    username: string
    idToken: string
    accessToken: string
    refreshToken: string
    tokenExpiration: number
    customClaims?: Record<string, unknown>
    secondaryAccessToken?: string | null
    exchangeToken?: string | null
  }
) =>
  state.merge({
    error: null,
    username,
    idToken,
    customClaims,
    refreshToken,
    tokenExpiration,
    accessToken,
    // Storing 2 access tokens is required when linking accounts
    secondaryAccessToken,
    // Storing exchange token is set if given
    exchangeToken,
    // Remove the password from the store
    password: undefined,
    deviceId: state.deviceId || DeviceInfo.getUniqueId(),
  })

export const failure = (state: ImmutableLoginState, { error }: { error: string }) =>
  state.merge({ fetching: false, error })

export const fetchComplete = (state: ImmutableLoginState) => state.merge({ fetching: false })

export const logout = (state: ImmutableLoginState) => state.merge({ isLoggingOut: true })

export const setConnection = (state: ImmutableLoginState, { connection }: { connection: string }) =>
  state.merge({ connection })

export const clearConnection = (state: ImmutableLoginState) => state.merge({ connection: null })

export const resetLoginError = (state: ImmutableLoginState) => {
  return state.merge({
    error: null,
  })
}

export const cancelLoginRequest = (state: ImmutableLoginState) =>
  state.merge({ fetching: false, isLoggingIn: false, error: null })

export const refreshAuthSuccess = (
  state: ImmutableLoginState,
  {
    idToken,
    accessToken,
    refreshToken,
    tokenExpiration,
    customClaims = {},
  }: {
    idToken: string
    accessToken: string
    refreshToken: string
    tokenExpiration: number
    customClaims?: Record<string, unknown>
  }
) =>
  state.merge({
    idToken,
    refreshToken,
    accessToken,
    tokenExpiration,
    customClaims,
  })

export const refreshAuthFailure = (state: ImmutableLoginState, { error }: { error: string }) =>
  state.merge({
    error,
  })

export const setMessagingCredentials = (
  state: ImmutableLoginState,
  action: { userData: { id: string; props: { team_id: string } } }
) => {
  const userData = action.userData
  return state.merge({
    messagingUserId: userData.id,
    messagingTeamId: userData.props.team_id,
  })
}

export const emailVerificationCheck = (
  state: ImmutableLoginState,
  { silent = false }: { silent?: boolean }
) =>
  state.merge({
    emailVerificationRunning: true,
    emailVerificationCheckSilent: silent,
  })
export const emailVerificationCheckFailure = (state: ImmutableLoginState) =>
  state.merge({
    emailVerificationRunning: false,
    emailVerificationCheckSilent: false,
  })
export const emailVerificationCheckSuccess = (state: ImmutableLoginState) =>
  state.merge({
    emailVerificationRunning: false,
    emailVerificationCheckSilent: false,
  })

export const emailVerificationResendRequest = (state: ImmutableLoginState) =>
  state.merge({
    emailVerificationResendRunning: true,
    emailVerificationResendFailed: false,
  })
export const emailVerificationResendFailure = (state: ImmutableLoginState) =>
  state.merge({
    emailVerificationResendRunning: false,
    emailVerificationResendFailed: true,
  })
export const emailVerificationResendSuccess = (state: ImmutableLoginState) =>
  state.merge({ emailVerificationResendRunning: false })

export const toggleMfaRequest = (state: ImmutableLoginState) =>
  state.merge({ toggleMfaRequestRunning: true, toggleMfaRequestFailed: false })

export const toggleMfaSuccess = (state: ImmutableLoginState) =>
  state.merge({ toggleMfaRequestRunning: false, toggleMfaRequestFailed: false })

export const toggleMfaFailure = (state: ImmutableLoginState) =>
  state.merge({ toggleMfaRequestRunning: false, toggleMfaRequestFailed: true })

export const setIsLoggingIn = (state: ImmutableLoginState, action: { data: boolean }) =>
  state.merge({ isLoggingIn: action.data })

export const setIsLoggingOut = (state: ImmutableLoginState, action: { data: boolean }) =>
  state.merge({ isLoggingOut: action.data })

export const setIsOnboardingInProgress = (state: ImmutableLoginState, action: { data: boolean }) =>
  state.merge({ isOnboardingInProgress: action.data })

export const clearState = (state: ImmutableLoginState) =>
  state.merge({ ...INITIAL_STATE, deviceId: state.deviceId })

export const submitPublicComputer = (
  state: ImmutableLoginState,
  action: { data: { publicComputer: string } }
) => state.merge({ publicComputer: action.data.publicComputer })

export const updateLastActivity = (state: ImmutableLoginState) =>
  state.merge({ lastActivity: Date.now() })

export const setEmailVerificationBlock = (
  state: ImmutableLoginState,
  { emailVerificationBlock }: { emailVerificationBlock: boolean }
) => {
  return state.merge({ emailVerificationBlock })
}

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer<TPayloads, TActions, ImmutableLoginState>(INITIAL_STATE, {
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.REHYDRATE_REDUCER]: rehydrateReducer,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.EMAIL_VERIFICATION_CHECK]: emailVerificationCheck,
  [Types.EMAIL_VERIFICATION_CHECK_FAILURE]: emailVerificationCheckFailure,
  [Types.EMAIL_VERIFICATION_CHECK_SUCCESS]: emailVerificationCheckSuccess,
  [Types.EMAIL_VERIFICATION_RESEND_REQUEST]: emailVerificationResendRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.EMAIL_VERIFICATION_RESEND_FAILURE]: emailVerificationResendFailure,
  [Types.EMAIL_VERIFICATION_RESEND_SUCCESS]: emailVerificationResendSuccess,
  [Types.LOGIN_REQUEST]: loginRequest,
  [Types.LOGIN_REQUEST_CANCELLED]: cancelLoginRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.ACCOUNT_LINK_LOGIN_REQUEST]: loginRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.LOGIN_REQUEST_WITH_CONNECTION]: loginRequestWithConnection,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SILENT_LOGIN_REQUEST_WITH_CONNECTION]: loginRequestWithConnection,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.LOGIN_REQUEST_WITH_EXCHANGE_TOKEN]: loginRequestWithExchangeToken,
  [Types.SIGNUP_REQUEST]: signupRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SIGNUP_REQUEST_WITH_CONNECTION]: signupRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.LOGIN_SUCCESS]: success,
  [Types.CLEAR_SECONDARY_ACCESS_TOKEN]: clearSecondaryAccessToken,
  [Types.LOGIN_FETCH_COMPLETE]: fetchComplete,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.LOGIN_FAILURE]: failure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_CONNECTION]: setConnection,
  [Types.CLEAR_CONNECTION]: clearConnection,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_MESSAGING_CREDENTIALS]: setMessagingCredentials,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.REFRESH_AUTH_SUCCESS]: refreshAuthSuccess,
  [Types.MANDATORY_MFA_REQUEST]: toggleMfaRequest,
  [Types.TOGGLE_MFA_REQUEST]: toggleMfaRequest,
  [Types.TOGGLE_MFA_SUCCESS]: toggleMfaSuccess,
  [Types.TOGGLE_MFA_FAILURE]: toggleMfaFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.LOGOUT]: logout,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_IS_LOGGING_IN]: setIsLoggingIn,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_IS_LOGGING_OUT]: setIsLoggingOut,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_IS_ONBOARDING_IN_PROGRESS]: setIsOnboardingInProgress,
  [Types.CLEAR_STATE]: clearState,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SUBMIT_PUBLIC_COMPUTER]: submitPublicComputer,
  [Types.UPDATE_LAST_ACTIVITY]: updateLastActivity,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_EMAIL_VERIFICATION_BLOCK]: setEmailVerificationBlock,
})

// TODO: relocate these selectors to App/Store/Login
export const selectLoginState = (state: { login: LoginState }) => state.login
export const selectAccessToken = (state: { login: LoginState }) => state.login.accessToken
export const selectMattermostUserId = (state: { login: LoginState }) => state.login.messagingUserId
export const selectMattermostTeamId = (state: { login: LoginState }) => state.login.messagingTeamId
